import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { APIService } from 'src/api/api';
import { TRANSACTION_TYPES } from 'src/utils/constants';
import { updateItemInArray } from 'src/utils/getInitials';

const getDetailsRequestKey = (transactionType, transactionId) => (
  `${transactionType}:${transactionId}`
);

const initialState = {
  totalCount: 0,
  transactionList: [],
  transactionListCache: {},
  activeListCacheKey: null,
  transactionDetails: {},
  transactionType: TRANSACTION_TYPES[0].id,
  listLoading: false,
  listRefreshing: false,
  detailsLoading: false,
  currentDetailsRequestKey: null,
  currentListRequestId: null,
};

export const getTransactionList = createAsyncThunk(
  'transaction/list', async (req, { getState, rejectWithValue }) => {
    const {
      transactionType, limit, pageNo, params, cacheKey, forceRefresh
    } = req;
    const cachedResult = !forceRefresh && cacheKey
      ? getState().transactions.transactionListCache[cacheKey]
      : null;
    if (cachedResult) return cachedResult;
    try {
      const res = await APIService.getTransactionList(transactionType, limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const loadMoreTransactionList = createAsyncThunk(
  'transaction/list/loadmore', async (req, { getState, rejectWithValue }) => {
    const {
      transactionType, limit, pageNo, params, cacheKey, forceRefresh
    } = req;
    const cachedResult = !forceRefresh && cacheKey
      ? getState().transactions.transactionListCache[cacheKey]
      : null;
    if (cachedResult) return cachedResult;
    try {
      const res = await APIService.getTransactionList(transactionType, limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getTransactionDetails = createAsyncThunk(
  'transaction/details', async (req, { rejectWithValue }) => {
    const { transactionType, transactionId } = req;
    try {
      const res = await APIService.getTransactionDetails(transactionType, transactionId);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateTransactionDetails = createAsyncThunk(
  'transaction/updateDetails', async (req, { rejectWithValue }) => {
    const { transactionType, values } = req;
    try {
      const res = await APIService.updateTransactionDetails(transactionType, values.id, values);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const addNewTransaction = createAsyncThunk(
  'transaction/create', async (req, { rejectWithValue },) => {
    const { transactionType, values } = req;
    try {
      const res = await APIService.createTransaction(transactionType, values);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const deleteTransaction = createAsyncThunk(
  'transaction/delete', async (req, { rejectWithValue },) => {
    const { transactionType, transactionId } = req;
    try {
      const res = await APIService.deleteTransaction(transactionType, transactionId);
      if (!(res?.success ?? true)) throw res;
      return transactionId;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setTransactionType: (state, action) => ({ ...state, transactionType: action.payload }),
    invalidateTransactionListCache: (state) => ({
      ...state,
      transactionList: [],
      transactionListCache: {},
      activeListCacheKey: null,
      totalCount: 0,
      currentListRequestId: null,
    }),
  },
  extraReducers: (builder) => {
    // add your async reducers here
    builder
      .addCase(getTransactionList.pending, (state, action) => ({
        ...state,
        transactionList: action.meta.arg.cacheKey
          && state.transactionListCache[action.meta.arg.cacheKey]
          ? state.transactionListCache[action.meta.arg.cacheKey].results
          : [],
        totalCount: action.meta.arg.cacheKey
          && state.transactionListCache[action.meta.arg.cacheKey]
          ? state.transactionListCache[action.meta.arg.cacheKey].count
          : 0,
        activeListCacheKey: action.meta.arg.cacheKey || null,
        listLoading: !(action.meta.arg.cacheKey
          && state.transactionListCache[action.meta.arg.cacheKey]),
        listRefreshing: Boolean(action.meta.arg.cacheKey
          && state.transactionListCache[action.meta.arg.cacheKey]
          && action.meta.arg.forceRefresh),
        currentListRequestId: action.meta.requestId,
      }))
      .addCase(getTransactionList.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentListRequestId) return state;
        const results = action.payload.results.slice().sort((a, b) => a.id - b.id);
        const cacheKey = action.meta.arg.cacheKey || null;
        return {
          ...state,
          transactionList: results,
          totalCount: action.payload.count,
          transactionListCache: cacheKey
            ? {
              ...state.transactionListCache,
              [cacheKey]: { results, count: action.payload.count },
            }
            : state.transactionListCache,
          activeListCacheKey: cacheKey,
          listLoading: false,
          listRefreshing: false,
          currentListRequestId: null,
        };
      })
      .addCase(getTransactionList.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentListRequestId) return state;
        return {
          ...state,
          listLoading: false,
          listRefreshing: false,
          currentListRequestId: null,
        };
      })
      .addCase(loadMoreTransactionList.pending, (state, action) => ({
        ...state,
        listLoading: true,
        listRefreshing: false,
        currentListRequestId: action.meta.requestId,
      }))
      .addCase(loadMoreTransactionList.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentListRequestId) return state;
        const results = action.payload.results.slice().sort((a, b) => a.id - b.id);
        const cacheKey = action.meta.arg.cacheKey || null;
        return {
          ...state,
          transactionList: results,
          totalCount: action.payload.count,
          transactionListCache: cacheKey
            ? {
              ...state.transactionListCache,
              [cacheKey]: { results, count: action.payload.count },
            }
            : state.transactionListCache,
          activeListCacheKey: cacheKey,
          listLoading: false,
          listRefreshing: false,
          currentListRequestId: null,
        };
      })
      .addCase(loadMoreTransactionList.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentListRequestId) return state;
        return {
          ...state,
          listLoading: false,
          listRefreshing: false,
          currentListRequestId: null,
        };
      })
      .addCase(getTransactionDetails.fulfilled, (state, action) => {
        const requestKey = getDetailsRequestKey(
          action.meta.arg.transactionType,
          action.meta.arg.transactionId
        );
        if (requestKey !== state.currentDetailsRequestKey) return state;
        return {
          ...state,
          detailsLoading: false,
          currentDetailsRequestKey: requestKey,
          transactionDetails: action.payload
        };
      })
      .addCase(getTransactionDetails.pending, (state, action) => ({
        ...state,
        detailsLoading: true,
        currentDetailsRequestKey: getDetailsRequestKey(
          action.meta.arg.transactionType,
          action.meta.arg.transactionId
        ),
        transactionDetails: {},
      }))
      .addCase(getTransactionDetails.rejected, (state, action) => ({
        ...state,
        detailsLoading: false,
        currentDetailsRequestKey: state.currentDetailsRequestKey === getDetailsRequestKey(
          action.meta.arg.transactionType,
          action.meta.arg.transactionId
        ) ? null : state.currentDetailsRequestKey,
      }))
      .addCase(updateTransactionDetails.fulfilled, (state, action) => ({
        ...state,
        detailsLoading: false,
        transactionDetails: action.payload,
        transactionListCache: {},
        transactionList: updateItemInArray(state.transactionList, action.payload)
      }))
      .addCase(addNewTransaction.fulfilled, (state, action) => ({
        ...state,
        transactionListCache: {},
        transactionList: [...state.transactionList, action.payload]
      }))
      .addCase(deleteTransaction.fulfilled, (state, action) => ({
        ...state,
        transactionListCache: {},
        transactionList: state.transactionList.filter((item) => item.id !== action.payload),
        totalCount: Math.max(0, state.totalCount - 1)
      }));
  }
});

// Action creators are generated for each case reducer function
export const { invalidateTransactionListCache, setTransactionType } = transactionSlice.actions;

export default transactionSlice.reducer;
